<template>
  <fragment>
    <split-modal
      ref="modal"
      modal-class="add-case-to-collection-modal"
      :title="$t('cmrad.add_case_to_collection')"
      title-icon="fa-clipboard-list"
      ok-variant="light"
      :value="!createModalOpen && (value || saving)"
      :ok-disabled="saving"
      v-bind="$attrs"
      @input="$emit('input', $event)"
      @hidden="onHidden"
    >
      <b-form-group>
        <b-form-input v-model="keywords" placeholder="Search your collections..." class="my-2" debounce="300" />
      </b-form-group>
      <b-list-group
        v-if="!isFetching && !saving && collections && collections.length"
        flush
        class="collections-list border-0"
      >
        <b-list-group-item
          v-for="(collection, index) in collections"
          :key="collection.id"
          class="d-flex flex-row align-items-center justify-content-between mb-2"
        >
          <collection-media :collection="collection"></collection-media>
          <btn
            icon
            icon-name="fa-plus"
            variant="primary"
            fab
            :disabled="saving"
            class="custom-color"
            :id="`add-collection-plus-btn-${index}`"
            @click.stop.prevent="addCaseToCollection(collection)"
          >
            <template #btnText>
              <span class="sr-only">Add to collection</span>
            </template>
          </btn>
          <b-tooltip :target="`add-collection-plus-btn-${index}`" triggers="hover">
            {{ $t('cmrad.add_case_to_collection') }}
          </b-tooltip>
        </b-list-group-item>
      </b-list-group>
      <empty-state
        v-else-if="!isFetching && !saving"
        class="border-0"
        svg-icon
        :font-icon="false"
        title="No collections found"
      >
        <template name="svg">
          <no-results-icon />
        </template>
      </empty-state>

      <empty-state v-if="isFetching" title="Loading" class="border-0">
        <template #image>
          <div class="loader position-relative text-center p-3">
            <b-spinner variant="primary" class="loading-spinner" :label="$t('cmrad.loading_with_dots')"></b-spinner>
          </div>
        </template>
      </empty-state>

      <empty-state v-if="saving" title="Adding case to collection" class="border-0">
        <template #image>
          <div class="loader position-relative text-center p-3">
            <b-spinner variant="primary" class="loading-spinner" label="Adding case to collection..."></b-spinner>
          </div>
        </template>
      </empty-state>

      <div class="d-flex align-items-center justify-content-end mt-2">
        <btn
          variant="outline-primary"
          icon
          icon-name="fa-plus"
          :disabled="isFetching || saving"
          :title="$t('cmrad.create_new_collection')"
          :btn-text="$t('cmrad.create_new_collection')"
          @click="onCreateCollection"
        ></btn>
      </div>

      <template #modal-footer>
        <cancel-btn size="sm" @click="onCancel"></cancel-btn>
      </template>
    </split-modal>
    <create-collection-modal
      v-model="createModalOpen"
      @saved="onCollectionCreated"
      @hidden="createModalOpen = false"
    ></create-collection-modal>
  </fragment>
</template>

<script>
  import { CaseResource } from '@/api-resources';
  import { mapState } from 'vuex';
  import toastr from 'toastr';

  export default {
    name: 'CollectionAddCaseModal',

    components: {
      SplitModal: () => import('@/Atoms/SplitModal'),
      CollectionMedia: () => import('@/Molecules/CollectionMedia'),
      EmptyState: () => import('@/Atoms/EmptyState'),
      NoResultsIcon: () => import('@/Atoms/svgIcons/NoResultsIcon'),
      CreateCollectionModal: () => import('@/Organisms/CreateCollectionModal'),
    },

    props: {
      originCase: CaseResource,
      value: Boolean,
    },

    data() {
      return {
        show: false,
        saving: false,
        createModalOpen: false,
        keywords: '',
      };
    },

    computed: {
      ...mapState({
        currentUser: (state) => state.currentUserResource,
      }),

      isFetching() {
        return this.$asyncComputed.collections.updating || !this.$asyncComputed.collections;
      },
    },

    asyncComputed: {
      async collections() {
        if (!this.value) {
          return null;
        }

        const response = await this.$api('users.collections', {
          version: '1.0',
          radiologist: this.currentUser.id,
          without_case: this.originCase.id,
          keywords: this.keywords,
          where_member: true,
        });

        return response.data.data;
      },
    },

    methods: {
      onHidden(evt) {
        this.$emit('input', false);
        this.$emit('hidden', evt);
      },

      async addCaseToCollection(collection) {
        this.saving = true;

        try {
          await this.$api(
            'case_collections.add_case',
            { version: '1.0', case_collection: collection.id },
            { case_id: this.originCase.id }
          );
          toastr.success(
            this.originCase.title + ' ' + this.$t('cmrad.case_add_to_collection') + ' ' + collection.title
          );
          this.$refs.modal.hide();
        } catch (err) {
          console.error(err);
          toastr.error(this.$t('cmrad.case_add_to_collection_error'));
        }

        this.saving = false;
        this.$asyncComputed.collections.update();
      },

      onCreateCollection() {
        this.createModalOpen = true;
      },

      onCollectionCreated(newCollection) {
        this.addCaseToCollection(newCollection);
      },

      onCancel() {
        this.$refs.modal.hide();
      },
    },
  };
</script>

<style lang="scss">
  .add-case-to-collection-modal {
    .loading-spinner {
      width: 5rem;
      height: 5rem;
    }
  }
</style>
